<template>
  <v-card class="border">
    <v-card-title>{{ $ucfirst($t('journeyBehaviourChangeGoals.behaviourChangeGoals')) }}</v-card-title>
    <v-simple-table class="patient-overview__table">
      <template v-slot:default>
        <tbody>
          <tr
            v-for="(item, index) in tableData"
            :key="`${item.label}-${index}`"
          >
            <td>
              <div class="d-flex align-center">
                <v-icon
                  v-if="!item.imgIcon"
                  class="patient-overview__icon"
                  color="rgba(0, 0, 0, 0.87)"
                >
                  {{ item.icon }}
                </v-icon>
                <img
                  v-else
                  :src="item.imgIcon"
                  class="
                    inline-block
                    patient-overview__icon
                    patient-overview-behaviour-change__img-icon
                  "
                />
                {{ item.label }}
              </div>
            </td>
            <td>
              <span
                :class="item.class"
                v-if="item.value"
              >{{ item.value }}</span>
            </td>
            <td>
              <span v-if="item.action">
                {{ item.action }}
              </span>
            </td>
          </tr>
        </tbody>
      </template>
    </v-simple-table>
  </v-card>
</template>

<script>
import { mapGetters } from 'vuex';

/**
 * Component PatientOverviewBehaviourChange.vue
 *
 * @module PatientOverviewBehaviourChange
 * @date
 * @author
 * @copyright UniWeb bvba 2022
 *
 * @link https://gitlab.uniweb.be/cor/4.04-caregiver-dashboard
 *
 * @coropreventionSW [SW-4.3] app/src/APP_DASHBOARD/templates/views/Patient/Overview/PatientOverviewBehaviourChange.vue
 * @see https://docs.google.com/document/d/1iOpHH151s_ABr-DniJIc__q-GHpXJJVq/edit#heading=h.a0z8d9xd3xr7
 *
 */

export default {
  name: 'PatientOverviewBehaviourChange',
  computed: {
    ...mapGetters({
      behaviourGoals: 'patientSummary/behaviourGoals',
      behaviourComponents: 'patientSummary/behaviourComponents',
      knowledgeLevel: 'patientSummary/knowledgeLevelKey',
      knowledgeLevelName: 'patientSummary/knowledgeLevelName'
    }),
    tableData() {
      return [
        {
          icon: 'mdi-pill',
          label: this.$ucfirst(this.$t('medication.medicationAdherence')),
          value: this.medicationValue,
          action: this.medicationAction,
          class: this.medicationClass,
        },
        {
          icon: 'mdi-run',
          label: this.$ucfirst(this.$t('screens.startMovingSettingsScreen', { catalog: 'app_mobile' })),
          value: this.startMovingValue,
          action: this.startMovingAction,
          class: this.startMovingClass,
        },
        {
          icon: 'mdi-silverware-fork-knife',
          label: this.$ucfirst(this.$t('screens.healthyNutritionSettingsScreen', { catalog: 'app_mobile' })),
          value: this.healthyNutritionValue,
          action: this.healthyNutritionAction,
          class: this.healthyNutritionClass,
        },
        {
          icon: 'mdi-smoking-off',
          label: this.$ucfirst(this.$t('screens.smokeFreeLivingSettingsScreen', { catalog: 'app_mobile' })),
          value: this.smokeCessationValue,
          action: this.smokeCessationAction,
          class: this.smokeCessationClass,
        },
        {
          icon: 'mdi-meditation',
          label: this.$ucfirst(this.$t('screens.stressReliefSettingsScreen', { catalog: 'app_mobile' })),
          value: this.stressManagementValue,
          action: this.stressManagementAction,
          class: this.stressManagementClass,
        },
        {
          icon: 'mdi-book-open-blank-variant',
          label: this.$ucfirst(this.$t('behaviourChangeGoals.knowledgelevel')),
          value: this.knowledgeValue,
          class: this.knowledgeClass,
          imgIcon: this.knowledgeIcon
        },
      ];
    },
    medicationValue() {
      return this.getValue(this.behaviourGoals.get('MEDICATIONS'));
    },
    medicationAction() {
      return this.getAction(this.behaviourComponents.get('MEDICATIONS'));
    },
    medicationClass() {
      return this.getClass(this.behaviourGoals.get('MEDICATIONS'));
    },
    startMovingValue() {
      return this.getValue(this.behaviourGoals.get('STARTMOVING'));
    },
    startMovingAction() {
      return this.getAction(this.behaviourComponents.get('STARTMOVING'));
    },
    startMovingClass() {
      return this.getClass(this.behaviourGoals.get('STARTMOVING'));
    },
    healthyNutritionValue() {
      return this.getValue(this.behaviourGoals.get('HEALTHYNUTRITION'));
    },
    healthyNutritionAction() {
      return this.getAction(this.behaviourComponents.get('HEALTHYNUTRITION'));
    },
    healthyNutritionClass() {
      return this.getClass(this.behaviourGoals.get('HEALTHYNUTRITION'));
    },
    smokeCessationValue() {
      return this.getValue(this.behaviourGoals.get('SMOKECESSATION'));
    },
    smokeCessationAction() {
      return this.getAction(this.behaviourComponents.get('SMOKECESSATION'));
    },
    smokeCessationClass() {
      return this.getClass(this.behaviourGoals.get('SMOKECESSATION'));
    },
    stressManagementValue() {
      return this.getValue(this.behaviourGoals.get('STRESSMANAGEMENT'));
    },
    stressManagementAction() {
      return this.getAction(this.behaviourComponents.get('STRESSMANAGEMENT'));
    },
    stressManagementClass() {
      return this.getClass(this.behaviourGoals.get('STRESSMANAGEMENT'));
    },
    knowledgeValue() {
      return this.knowledgeLevelName;
    },
    knowledgeClass() {
      let knowledgeClass = this.getClass(this.behaviourGoals.get('KNOWLEDGE'));
      if (!knowledgeClass && this.knowledgeLevel === 'beginner') {
        return 'patient-overview-parameter--red'
      }
      return knowledgeClass
    },
    knowledgeIcon() {
      const level = this.knowledgeLevel;
      switch(level) {
        case 'beginner':
          return this.$asset('image/beginner.png');
        case 'advanced':
          return this.$asset('image/advanced.png');
        case 'expert':
          return this.$asset('image/expert.png');
        default:
          return this.$asset('image/beginner.png');
      }
    }
  },
  methods: {
    getValue(behaviourGoal) {
      if (!behaviourGoal) return '';
      if (behaviourGoal.key == 'SMOKECESSATION') {
        if (behaviourGoal.value == 'active_smoker') {

          return this.$ucfirst(this.$t('smokingRiskProfile.activeSmoker',
              {
                params: {
                  dependence: this.$t(behaviourGoal.extraInformation.dependence)
                }
              }
          ));
        } else {
          return behaviourGoal.label || '';
        }
      }
      return behaviourGoal.label || '';
    },
    getAction(behaviourComponent) {
      if (!behaviourComponent) return '';

      // For "MEDICATIONS": startAction -> monitoredAction; monitoredAction -> maintainedBehavior.
      if (behaviourComponent.key == "MEDICATIONS") {
        if (behaviourComponent.levelOfGuidance == 'startAction') {
          return this.$ucfirst(this.$t(`journeyBehaviourChangeGoals.monitoredAction`, { catalog: 'app_dashboard' }));
        }
        if (behaviourComponent.levelOfGuidance == 'monitoredAction') {
          return this.$ucfirst(this.$t(`journeyBehaviourChangeGoals.maintainedBehavior`, { catalog: 'app_dashboard' }));
        }
      }

      // If not 0 return translated action string, else return translated 'inactive';
      if (behaviourComponent.levelOfGuidance) {
        return this.$ucfirst(this.$t(`journeyBehaviourChangeGoals.${behaviourComponent.levelOfGuidance}`, { catalog: 'app_dashboard' }));
      } else {
        return this.$ucfirst(this.$t(`physicalActivityStepGoals.inactive`, { catalog: 'app_mobile' }));
      }
    },
    getClass(behaviourGoal) {
      if (!behaviourGoal || !behaviourGoal.color) return '';
      return `patient-overview-parameter--${behaviourGoal.color}`;
    }
  },
};
</script>

<style lang="scss">
.patient-overview-behaviour-change__names,
.patient-overview-behaviour-change__status {
  margin-right: 50px;
}

.patient-overview-behaviour-change__img-icon {
  width: 100%;
  max-width: 24px;
  border-radius: 50%;
}
</style>
